import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import useIsMobile from "../../hooks/useIsMobile";
import { readTour, subscribeTour, writeTour } from "./tourState";
import styles from "./welcome.module.css";

// Loaded on demand: it pulls in the product API, which the layout must not import up front
// (the product slice and the API import each other).
const FirstQuotation = lazy(() => import("./FirstQuotation"));

const nav = (link) => `aside[aria-label="Navigation"] a[href="${link}"]`;
const byText = (selector, text) => () =>
  [...document.querySelectorAll(selector)].find((el) => el.textContent.trim().startsWith(text));

// One entry per screen of the walkthrough. `route` is where it happens, `target` what lights
// up, and `tap` means Next really clicks the target (open the menu, follow a link).
const buildSteps = (mobile) => [
  ...(mobile
    ? [
        {
          route: "/customer/dashboard",
          target: 'button[aria-label="Open menu"]',
          tap: true,
          title: "Everything starts in the menu",
          text: "Tap the menu to see every part of U-Send: products, orders, payments and settings.",
        },
      ]
    : []),
  {
    route: "/customer/dashboard",
    target: nav("/customer/products"),
    tap: true,
    title: "Your products",
    text: "Everything from your Shopify store is under Products. Let’s open it.",
  },
  {
    route: "/customer/products/live-products",
    target: [byText("button", "Get quote"), ".product_card"],
    title: "Ask for a quotation",
    text: "These are your Shopify products. Tap Get quote on one and we find the best product and shipping price. You get a notification when it is ready.",
  },
  {
    route: "/customer/orders",
    target: '[class*="statusStrip_strip"]',
    title: "Your orders",
    text: "Orders from Shopify come in here by themselves. You see what waits for payment, what we pack, what has shipped and what needs a fix.",
  },
  {
    route: "/customer/orders",
    target: [byText("button", "Fulfill"), '[class*="statusStrip_strip"]'],
    title: "Fulfill with one tap",
    text: "Select waiting orders and tap Fulfill. You pick regular or premium shipping, pay from your wallet and we ship them.",
  },
  {
    route: "/customer/payments",
    target: '[class*="paymentsOverview_balances"]',
    title: "Your wallet",
    text: "Orders are paid from your wallet balance, so keep enough in it.",
  },
  {
    route: "/customer/payments",
    target: ['[class*="paymentsOverview_topUp"]', '[class*="paymentsOverview_balances"]'],
    title: "Top up",
    text: "Top up by bank transfer or PayPal so your orders ship without waiting.",
  },
];

const find = (target) => {
  for (const t of Array.isArray(target) ? target : [target]) {
    const el = typeof t === "function" ? t() : document.querySelector(t);
    const r = el?.getBoundingClientRect();
    if (r && r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth) return el;
  }
  return null;
};

const PAD = 6;

// The walkthrough after the welcome flow. Mounted by the customer layout on every page; the
// explanation sits at the bottom while the screen above moves through the app for real.
export default function WelcomeTour() {
  const [tour, setTour] = useState(readTour);
  useEffect(() => subscribeTour(setTour), []);

  if (!tour) return null;
  if (tour.stage === "quote") {
    return (
      <Suspense fallback={null}>
        <FirstQuotation demo={tour.demo} onClose={() => writeTour(null)} />
      </Suspense>
    );
  }
  return <TourSteps tour={tour} />;
}

function TourSteps({ tour }) {
  const mobile = useIsMobile();
  const steps = useMemo(() => buildSteps(mobile), [mobile]);
  const index = Math.min(tour.step || 0, steps.length - 1);
  const step = steps[index];
  const last = index === steps.length - 1;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [rect, setRect] = useState(null);
  const [tapping, setTapping] = useState(false);
  const elRef = useRef(null);

  const go = useCallback((i) => writeTour({ ...tour, step: i }), [tour]);
  const finish = useCallback(() => writeTour({ stage: "quote", demo: tour.demo }), [tour.demo]);

  // Bring the right page up first.
  useEffect(() => {
    if (pathname !== step.route) navigate(step.route);
  }, [pathname, step.route, navigate]);

  // Pages load their data after mounting, so look for the target for a few seconds.
  useLayoutEffect(() => {
    setRect(null);
    elRef.current = null;
    if (pathname !== step.route) return undefined;
    let tries = 0;
    let timer;
    const measure = () => {
      const el = elRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    };
    const look = () => {
      const el = find(step.target);
      if (el) {
        elRef.current = el;
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        measure();
        timer = setTimeout(measure, 400);
      } else if (tries++ < 20) {
        timer = setTimeout(look, 200);
      }
    };
    look();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [index, pathname, step.route, step.target]);

  const next = () => {
    if (tapping) return;
    if (step.tap && elRef.current) {
      // Show the tap, then really click so the menu opens or the page changes.
      setTapping(true);
      const el = elRef.current;
      setTimeout(() => {
        setTapping(false);
        el.click();
        if (last) finish();
        else go(index + 1);
      }, 550);
      return;
    }
    if (last) finish();
    else go(index + 1);
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && finish();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [finish]);

  return createPortal(
    <div role="dialog" aria-modal="true" aria-labelledby="welcome-tour-title" className={styles.tourRoot}>
      <div className={styles.tourShield} />
      {rect ? (
        <div className={styles.tourSpot} style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}>
          {step.tap && <span className={`${styles.tap} ${tapping ? styles.tapNow : ""}`} aria-hidden="true" />}
        </div>
      ) : (
        <div className={styles.tourDim} />
      )}
      <section className={styles.tourSheet}>
        <div className={styles.tourTop}>
          <span>
            Step {index + 1} of {steps.length}
          </span>
          <span className={styles.tourDots} aria-hidden="true">
            {steps.map((s, i) => (
              <i key={i} className={i === index ? styles.on : ""} />
            ))}
          </span>
        </div>
        <h2 id="welcome-tour-title">{step.title}</h2>
        <p>{step.text}</p>
        <div className={styles.tourActions}>
          {index === 0 ? (
            <button type="button" className={styles.textButton} onClick={finish}>
              Skip tour
            </button>
          ) : (
            <button type="button" className={styles.textButton} onClick={() => go(index - 1)}>
              Back
            </button>
          )}
          <button type="button" className={styles.primary} onClick={next} autoFocus>
            {last ? "Got it" : step.tap ? (mobile && index === 0 ? "Open the menu" : "Open it") : "Next"}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}

export const startWelcomeTour = (demo) => writeTour({ step: 0, demo: !!demo });
export const startFirstQuotation = (demo) => writeTour({ stage: "quote", demo: !!demo });
